import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, Plus, Download } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { downloadCsv } from "@/lib/csv-export";
import {
  binderyApi,
  type BinderyRequestSummary,
  type BinderyRequestStatus,
} from "@/lib/api/bindery";
import { BinderyRequestStatusBadge } from "@/components/bindery/BinderyStatusBadge";
import { formatDate, formatMoney } from "./binderyFormatters";

const STATUS_OPTIONS: BinderyRequestStatus[] = [
  "DRAFT",
  "SENT",
  "IN_REPAIR",
  "RETURNED",
  "CLOSED",
  "CANCELLED",
];

export default function BinderyRequestsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<BinderyRequestSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [overdueOnly, setOverdueOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await binderyApi.requests.list({
        search: search || undefined,
        status: statusFilter !== "all" ? (statusFilter as BinderyRequestStatus) : undefined,
        overdue: overdueOnly ? "true" : undefined,
        limit: 50,
      });
      setData(res.requests);
      setTotal(res.total);
    } catch (err) {
      console.error("Failed to load bindery requests", err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, overdueOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = () => {
    const headers = ["Request", "Vendor", "Items", "Status", "Sent", "Expected", "Cost"];
    const rows = data.map((r) => [
      r.requestNumber,
      r.vendor.name,
      String(r._count.items),
      r.status,
      r.sentAt ?? "",
      r.expectedReturnAt ?? "",
      String(r.costTotal),
    ]);
    downloadCsv(`bindery-requests-${new Date().toISOString().slice(0, 10)}`, headers, rows);
  };

  const columns: DataTableColumn<BinderyRequestSummary>[] = [
    {
      id: "number",
      header: "Request",
      cell: (r) => (
        <button
          onClick={() => navigate(`/bindery/requests/${r.id}`)}
          className="font-mono text-primary hover:underline"
        >
          {r.requestNumber}
        </button>
      ),
    },
    {
      id: "vendor",
      header: "Vendor",
      cell: (r) => <span className="text-sm text-slate-700">{r.vendor.name}</span>,
    },
    {
      id: "items",
      header: "Items",
      cell: (r) => <span className="text-sm tabular-nums">{r._count.items}</span>,
      width: "w-16",
      align: "center",
      hideOnMobile: true,
    },
    {
      id: "status",
      header: "Status",
      cell: (r) => <BinderyRequestStatusBadge status={r.status} />,
      width: "w-44",
    },
    {
      id: "sent",
      header: "Sent",
      cell: (r) => <span className="text-xs text-slate-500">{formatDate(r.sentAt)}</span>,
      width: "w-28",
      hideOnMobile: true,
    },
    {
      id: "expected",
      header: "Expected",
      cell: (r) => {
        if (!r.expectedReturnAt) return <span className="text-xs text-slate-400">—</span>;
        const overdue =
          new Date(r.expectedReturnAt) < new Date() &&
          (r.status === "SENT" || r.status === "IN_REPAIR");
        return (
          <span className={`text-xs ${overdue ? "font-semibold text-red-600" : "text-slate-500"}`}>
            {formatDate(r.expectedReturnAt)}
          </span>
        );
      },
      width: "w-28",
      hideOnMobile: true,
    },
    {
      id: "cost",
      header: "Cost",
      cell: (r) => <span className="text-sm tabular-nums">{formatMoney(r.costTotal)}</span>,
      width: "w-24",
      align: "right",
      hideOnMobile: true,
    },
  ];

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Bindery Requests"
        description="Batch items to repair vendors and track them through QC."
        breadcrumbItems={[
          { label: "Dashboard", href: "/dashboard" },
          { label: "Bindery requests" },
        ]}
        titleSize="md"
        searchQuery={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by request number, shipping ref, vendor..."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={loading || data.length === 0}
              onClick={exportCsv}
            >
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
            <Button onClick={() => navigate("/bindery/requests/new")}>
              <Plus className="mr-2 h-4 w-4" /> New request
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {s.replace(/_/g, " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={overdueOnly}
            onChange={(e) => setOverdueOnly(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300"
          />
          Overdue only
        </label>
        <span className="text-sm text-slate-500">
          {total} request{total !== 1 ? "s" : ""}
        </span>
      </div>

      <DataTable
        columns={columns}
        data={data}
        rowKey={(r) => r.id}
        loading={loading}
        emptyState={
          <EmptyState
            icon={<Wrench className="h-6 w-6 text-slate-400" />}
            title="No bindery requests yet"
            description="Flag damaged items and send them to a bindery vendor for repair."
            action={
              <Button onClick={() => navigate("/bindery/requests/new")}>
                <Plus className="mr-2 h-4 w-4" /> New request
              </Button>
            }
          />
        }
      />
    </PageContainer>
  );
}
